// src/pages/AccountPage.jsx
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import useWebSocket from '../hooks/useWebSocket';
import {
  WS_ACCOUNT_TYPES,
  getAccountAppEndpoint,
  getAccountUserQueueTopic,
  WS_ACCOUNT_TOPICS,
  WS_ACCOUNT_USER_TOPICS,
} from '../config/wsAccountConfig';

import PageTransition from '../components/Shared/PageTransition';
import LoadingSkeleton from '../components/Shared/LoadingSkeleton';
import ModalWrapper from '../components/Shared/ModalWrapper';
import AccountHero from '../components/Account/AccountHero';
import AvailablePlans from '../components/Account/AvailablePlans';
import AccountTools from '../components/Account/AccountTools';
import PaymentHistory from '../components/Account/PaymentHistory';

import { useNotification } from '../contexts/NotificationContext';

import './AccountPage.css';

const AccountPage = ({ openModal, closeModal }) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';
  const { user, isAuthenticated, isAuthLoading, refreshUser, triggerGoogleLogin, isLoggingIn } = useAuth();
  const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
  const location = useLocation();
  const navigate = useNavigate();

  const [accountState, setAccountState] = useState({
    userData: null,
    subscription: null,
    stats: { bonusPoints: 0 },
    paymentHistory: [],
    availablePlans: [],
    apiKeys: [],
    referralLinks: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isProcessingReferral, setIsProcessingReferral] = useState(false);
  const [isProcessingApiKey, setIsProcessingApiKey] = useState(false);
  const [pendingModalAction, setPendingModalAction] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  // Withdraw Modal State
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(5000);
  const [withdrawMethod, setWithdrawMethod] = useState('usdt');
  const [withdrawAddress, setWithdrawAddress] = useState('');

  const { showToast } = useNotification();

  const subscriptions = useRef({
    state: null,
    status: null,
    error: null,
    apiKeys: null,
  });

  // Smooth scroll handler for incoming navigation
  useEffect(() => {
    if (location.state?.scrollTo === 'plans') {
      const element = document.getElementById('plansSection');
      setTimeout(() => element?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.scrollTo === 'api-keys') {
      const element = document.getElementById('apiKeysSection');
      setTimeout(() => element?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.scrollTo === 'referrals') {
      const element = document.getElementById('referralsSection');
      setTimeout(() => element?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Prompt restore plan modal for free users once (appearance suppressed by user request; code preserved)
  useEffect(() => {
    if (isAuthLoading) return;
    const isFreePlan = (user?.plan || 'free').toLowerCase() === 'free';
    const hasShown = localStorage.getItem('restore_plan_modal_shown');
    if (isFreePlan && !hasShown) {
      // setShowRestoreModal(true);
      localStorage.setItem('restore_plan_modal_shown', 'true');
    }
  }, [user, isAuthLoading]);

  // Merge incoming backend state
  const mergeAccountState = useCallback(
    (prevState, newServerState) => {
      const updateData = newServerState.data || newServerState;
      const mergedUserData = {
        ...(prevState?.userData || {}),
        ...(updateData.userData || {}),
        name: updateData.userData?.name || user?.name || prevState?.userData?.name || t('user'),
        email: updateData.userData?.email || user?.email || prevState?.userData?.email,
        id: updateData.userData?.id ?? user?.id ?? prevState?.userData?.id,
        dateCreated: updateData.userData?.dateCreated || prevState?.userData?.dateCreated,
      };

      const currentPlanType =
        updateData.subscription?.type ?? prevState?.subscription?.type ?? user?.plan ?? 'Free';
      const plansSource = Array.isArray(updateData.availablePlans)
        ? updateData.availablePlans
        : Array.isArray(prevState?.availablePlans)
        ? prevState.availablePlans
        : [];

      const updatedPlans = plansSource.map((plan) => {
        const isCurrentPlan = plan.name?.toLowerCase() === currentPlanType?.toLowerCase();
        return { ...plan, isCurrent: isCurrentPlan };
      });

      return {
        userData: mergedUserData,
        subscription:
          updateData.subscription !== undefined
            ? updateData.subscription
            : prevState?.subscription,
        stats: updateData.stats ?? prevState?.stats,
        paymentHistory: updateData.paymentHistory ?? prevState?.paymentHistory ?? [],
        availablePlans: updatedPlans,
        apiKeys: updateData.apiKeys ?? prevState?.apiKeys ?? [],
        referralLinks: updateData.referralLinks ?? prevState?.referralLinks ?? [],
      };
    },
    [user, t]
  );

  const handlePaymentMethodSubmission = useCallback(
    (submittedPlan, submittedActionType, selectedMethod) => {
      setIsProcessingAction(true);
      let paymentMethodIdToSend = selectedMethod?.id || null;
      setPendingModalAction((prev) => ({
        ...prev,
        plan: submittedPlan,
        type: submittedActionType,
        paymentMethod: selectedMethod,
        phase: 'awaiting_url',
      }));
      const topic =
        submittedActionType === 'upgrade'
          ? WS_ACCOUNT_TOPICS.UPGRADE
          : WS_ACCOUNT_TOPICS.EXTEND;
      sendMessage(getAccountAppEndpoint(topic), {
        planId: submittedPlan ? String(submittedPlan.name) : null,
        paymentMethodId: paymentMethodIdToSend,
      });
    },
    [sendMessage]
  );

  const handleStateUpdate = useCallback(
    (message) => {
      setIsLoading(false);
      if (!pendingModalAction) setIsProcessingAction(false);
      setAccountState((prev) => mergeAccountState(prev, message));
    },
    [mergeAccountState, pendingModalAction]
  );

  const handleStatusUpdate = useCallback(
    (message) => {
      const { type: messageType, data: actionData } = message;

      switch (messageType) {
        case WS_ACCOUNT_TYPES.ACTION_INITIATED:
          if (actionData?.paymentDetails?.paymentUrl && pendingModalAction?.plan) {
            setPendingModalAction((prev) => ({
              ...prev,
              phase: 'url_received',
              paymentDetails: actionData.paymentDetails,
            }));
          } else if (actionData?.action === 'cancel') {
            // Cancel acknowledged
          } else {
            setIsProcessingAction(false);
            setPendingModalAction(null);
            closeModal('payment');
            showToast(actionData?.message || t('couldNotProcessPaymentInitiation'), { type: 'error' });
          }
          break;
        case WS_ACCOUNT_TYPES.ACTION_SUCCESS:
          setIsProcessingAction(false);
          if (
            actionData?.action === pendingModalAction?.type &&
            (actionData.action === 'upgrade' || actionData.action === 'extend')
          ) {
            setPendingModalAction((prev) => ({
              ...prev,
              phase: 'completed_successfully',
              statusMessage: actionData.message,
              paymentDetails: null,
            }));
            refreshUser();
            showToast(isEn ? 'Subscription updated successfully!' : 'Тариф успешно обновлен!');
          } else {
            closeModal('payment');
            setPendingModalAction(null);
          }
          if (actionData?.updatedState) {
            setAccountState((prev) => mergeAccountState(prev, actionData.updatedState));
          } else if (isWebSocketConnected) {
            sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.GET_STATE), {});
          }
          break;
        case WS_ACCOUNT_TYPES.ACTION_FAILED:
          setIsProcessingAction(false);
          setPendingModalAction(null);
          showToast(actionData?.message || t('unknownErrorOccurred'), { type: 'error' });
          break;
        case WS_ACCOUNT_TYPES.REFERRALS_LIST:
          setIsProcessingReferral(false);
          setAccountState((prev) => ({
            ...prev,
            referralLinks: actionData || [],
          }));
          break;
        case WS_ACCOUNT_TYPES.PAYMENTS_LIST:
          setAccountState((prev) => ({
            ...prev,
            paymentHistory: actionData || [],
          }));
          break;
        default:
          break;
      }
    },
    [
      isWebSocketConnected,
      closeModal,
      sendMessage,
      mergeAccountState,
      pendingModalAction,
      t,
      refreshUser,
      showToast,
      isEn,
    ]
  );

  const handleErrorUpdate = useCallback(
    (message) => {
      showToast(message?.message || message?.error || t('unknownErrorOccurred'), { type: 'error' });
      setIsLoading(false);
      setIsProcessingAction(false);
      setPendingModalAction(null);
      closeModal('payment');
    },
    [closeModal, t, showToast]
  );

  const handleApiKeysStateUpdate = useCallback((message) => {
    setIsProcessingApiKey(false);
    setAccountState((prev) => ({
      ...prev,
      apiKeys: Array.isArray(message.data)
        ? message.data
        : message.data?.apiKeys || [],
    }));
  }, []);

  const handleCreateApiKey = useCallback(
    (name) => {
      if (!name || isProcessingApiKey) return;
      setIsProcessingApiKey(true);
      sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.API_KEYS_CREATE), { name });
    },
    [isProcessingApiKey, sendMessage]
  );

  const handleDeleteApiKey = useCallback(
    (keyId) => {
      setIsProcessingApiKey(true);
      sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.API_KEYS_DELETE), { keyId });
      showToast(isEn ? 'API key revoked' : 'API Ключ отозван');
    },
    [isProcessingApiKey, sendMessage, showToast, isEn]
  );

  const handleToggleApiKey = useCallback(
    (keyId, isActive) => {
      setIsProcessingApiKey(true);
      sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.API_KEYS_TOGGLE), { keyId, isActive });
    },
    [isProcessingApiKey, sendMessage]
  );

  const handleCreateReferralLink = useCallback(
    (name) => {
      if (!name || isProcessingReferral) return;
      setIsProcessingReferral(true);
      sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.REFERRALS_CREATE), { planId: name });
    },
    [isProcessingReferral, sendMessage]
  );

  const handleDeleteReferralLink = useCallback(
    (linkId) => {
      setIsProcessingReferral(true);
      sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.REFERRALS_DELETE), {
        paymentMethodId: String(linkId),
      });
      showToast(isEn ? 'Referral link deleted' : 'Реферальная ссылка удалена');
    },
    [isProcessingReferral, sendMessage, showToast, isEn]
  );

  const handlers = useRef({
    handleStateUpdate,
    handleStatusUpdate,
    handleErrorUpdate,
    handleApiKeysStateUpdate,
  });
  useEffect(() => {
    handlers.current = {
      handleStateUpdate,
      handleStatusUpdate,
      handleErrorUpdate,
      handleApiKeysStateUpdate,
    };
  }, [
    handleStateUpdate,
    handleStatusUpdate,
    handleErrorUpdate,
    handleApiKeysStateUpdate,
  ]);

  // Live WebSocket subscriptions to backend on port 8085
  useEffect(() => {
    if (isAuthLoading || !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    if (isWebSocketConnected) {
      setIsLoading(true);

      subscriptions.current.state = subscribe(
        getAccountUserQueueTopic(WS_ACCOUNT_USER_TOPICS.STATE),
        (m) => handlers.current.handleStateUpdate(m)
      );
      subscriptions.current.status = subscribe(
        getAccountUserQueueTopic(WS_ACCOUNT_USER_TOPICS.STATUS),
        (m) => handlers.current.handleStatusUpdate(m)
      );
      subscriptions.current.error = subscribe(
        getAccountUserQueueTopic(WS_ACCOUNT_USER_TOPICS.ERROR),
        (m) => handlers.current.handleErrorUpdate(m)
      );
      subscriptions.current.apiKeys = subscribe(
        getAccountUserQueueTopic(WS_ACCOUNT_USER_TOPICS.API_KEYS_STATE),
        (m) => handlers.current.handleApiKeysStateUpdate(m)
      );

      const timer = setTimeout(() => {
        if (isWebSocketConnected) {
          sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.GET_STATE), {});
          sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.API_KEYS_GET), {});
          sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.REFERRALS_GET), {});
          sendMessage(getAccountAppEndpoint(WS_ACCOUNT_TOPICS.PAYMENTS_GET), {});
        }
      }, 150);

      const currentSubs = subscriptions.current;
      return () => {
        clearTimeout(timer);
        Object.values(currentSubs).forEach((unsub) => unsub && unsub());
      };
    }
  }, [isWebSocketConnected, isAuthenticated, isAuthLoading, subscribe, sendMessage]);

  // Payment modal lifecycle
  useEffect(() => {
    if (!pendingModalAction) return;
    const { phase, plan, type, paymentDetails } = pendingModalAction;
    let modalProps = {
      plan,
      actionType: type,
      paymentDetails,
      onConfirmWithMethod: handlePaymentMethodSubmission,
      onClose: () => {
        closeModal('payment');
        setPendingModalAction(null);
      },
    };

    if (phase === 'completed_successfully') modalProps.initialPhase = 'payment_successful';
    else if (phase === 'url_received') modalProps.initialPhase = 'awaiting_payment_completion';
    else if (phase === 'awaiting_url') modalProps.initialPhase = 'initiating_payment';

    if (['method_selection', 'awaiting_url', 'url_received', 'completed_successfully'].includes(phase)) {
      openModal('payment', modalProps);
    }
  }, [pendingModalAction, openModal, closeModal, handlePaymentMethodSubmission]);

  const handleSelectPlan = (plan, actionType = 'upgrade') => {
    if (!isProcessingAction && plan) {
      const type = actionType === 'extend' || plan.isCurrent ? 'extend' : 'upgrade';
      const planNameLower = (plan.name || '').toLowerCase();
      const renewPrice =
        planNameLower === 'vip'
          ? '149 ₽'
          : planNameLower === 'premium'
          ? '199 ₽'
          : '299 ₽';
      const renewAmount =
        planNameLower === 'vip'
          ? 149
          : planNameLower === 'premium'
          ? 199
          : 299;

      const planToSubmit =
        type === 'extend'
          ? {
              ...plan,
              price: plan.renewPrice || renewPrice,
              amount: plan.renewAmount || renewAmount,
            }
          : plan;

      setPendingModalAction({ type, plan: planToSubmit, phase: 'method_selection' });
    }
  };

  const handleExtend = () => {
    if (!isProcessingAction && accountState.subscription) {
      const currentPlanType = accountState.subscription.type || user?.plan || 'VIP';
      const basePlan =
        accountState.availablePlans.find((p) => p.name?.toLowerCase() === currentPlanType.toLowerCase()) || {
          name: currentPlanType,
          isCurrent: true,
        };
      const planNameLower = (basePlan.name || '').toLowerCase();
      const renewPrice =
        planNameLower === 'vip'
          ? '149 ₽'
          : planNameLower === 'premium'
          ? '199 ₽'
          : '299 ₽';
      const renewAmount =
        planNameLower === 'vip'
          ? 149
          : planNameLower === 'premium'
          ? 199
          : 299;

      const plan = {
        ...basePlan,
        price: basePlan.renewPrice || renewPrice,
        amount: basePlan.renewAmount || renewAmount,
        isCurrent: true,
      };
      setPendingModalAction({ type: 'extend', plan, phase: 'method_selection' });
    }
  };

  const currentBalance = useMemo(() => {
    return (
      accountState.stats?.bonusPoints ??
      accountState.userData?.balance ??
      0
    );
  }, [accountState.stats, accountState.userData]);

  const handleWithdrawSubmit = (e) => {
    e.preventDefault();
    if (withdrawAmount <= 0 || withdrawAmount > currentBalance) {
      showToast(isEn ? 'Insufficient funds for withdrawal' : 'Недостаточно средств для вывода');
      return;
    }

    setAccountState((prev) => ({
      ...prev,
      stats: {
        ...(prev.stats || {}),
        bonusPoints: Math.max(0, currentBalance - withdrawAmount),
      },
    }));

    setIsWithdrawModalOpen(false);
    showToast(
      isEn
        ? `Withdrawal request for ${withdrawAmount.toLocaleString()} ₽ accepted!`
        : `Заявка на вывод ${withdrawAmount.toLocaleString()} ₽ успешно принята в обработку!`
    );
  };

  if (!isAuthLoading && !isAuthenticated) {
    return (
      <PageTransition>
        <div className="account-page-wrapper flex items-center justify-center px-4 py-20">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-3xl bg-white/[0.04] border border-white/10 flex items-center justify-center mx-auto mb-6">
              <svg viewBox="0 0 24 24" className="w-8 h-8 stroke-white fill-none stroke-[1.8]">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-white">
              {t('loginRequired')}
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {t('loginRequiredDescription')}
            </p>
            <button
              onClick={() => !isLoggingIn && triggerGoogleLogin()}
              disabled={isLoggingIn}
              className="inline-flex items-center justify-center gap-2.5 px-7 py-3 rounded-full bg-white text-black font-bold text-sm hover:bg-gray-200 transition-all shadow-[0_4px_20px_rgba(255,255,255,0.2)] active:scale-95 disabled:opacity-75"
            >
              {isLoggingIn ? (
                <>
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin shrink-0" />
                  <span>{t('loggingIn') || (isEn ? 'Signing in...' : 'Вход...')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                  <span>{t('loginWithGoogle') || (isEn ? 'Login with Google' : 'Войти через Google')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="account-page-wrapper">
        <div className="account-page-container">
          {/* 1. Profile Master Card (Email, ID, Tier, Change Plan) */}
          {isLoading && !accountState.userData && !user ? (
            <LoadingSkeleton className="h-[96px] rounded-[20px] mb-8" />
          ) : (
            <AccountHero
              userData={{ ...(user || {}), ...(accountState.userData || {}) }}
              subscription={accountState.subscription || { type: user?.plan || 'VIP' }}
              onManage={() => {
                const el = document.getElementById('plansSection');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
              onExtend={handleExtend}
              onShowToast={showToast}
            />
          )}

          {/* 2. Тарифные планы (Free, Vip, Premium, Professional) */}
          <AvailablePlans
            plans={accountState.availablePlans}
            currentPlan={accountState.subscription?.type || user?.plan || 'VIP'}
            onSelectPlan={handleSelectPlan}
            isLoading={isProcessingAction}
          />

          {/* 3. Рефералы и баланс + API Ключи */}
          <AccountTools
            apiKeys={accountState.apiKeys}
            onCreateKey={handleCreateApiKey}
            onDeleteKey={handleDeleteApiKey}
            onToggleKey={handleToggleApiKey}
            isProcessingApiKey={isProcessingApiKey}
            referralLinks={accountState.referralLinks}
            onCreateReferralLink={handleCreateReferralLink}
            onDeleteReferralLink={handleDeleteReferralLink}
            isProcessingReferral={isProcessingReferral}
            balance={currentBalance}
            onOpenWithdraw={() => setIsWithdrawModalOpen(true)}
            onShowToast={showToast}
          />

          {/* 4. Недавние платежи (Redesigned Apple/Grok Cyber-Table) */}
          <PaymentHistory payments={accountState.paymentHistory} />
        </div>

        {/* Модальное окно: Вывод средств (Apple Glass Card) */}
        {isWithdrawModalOpen && (
          <div
            className="acc-modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsWithdrawModalOpen(false);
            }}
          >
            <div className="acc-modal-card">
              <button
                type="button"
                className="acc-modal-close-btn"
                onClick={() => setIsWithdrawModalOpen(false)}
                title={isEn ? 'Close' : 'Закрыть'}
              >
                <svg viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <div className="acc-modal-header">
                <h3 className="acc-modal-title">
                  {isEn ? 'WITHDRAW FUNDS' : 'ВЫВОД СРЕДСТВ'}
                </h3>
                <p className="acc-modal-desc">
                  {isEn ? 'Account balance: ' : 'Баланс счета: '}
                  <strong style={{ color: '#ffffff' }}>
                    {currentBalance.toLocaleString()} ₽
                  </strong>
                </p>
              </div>

              <form className="acc-modal-form" onSubmit={handleWithdrawSubmit}>
                <div className="acc-modal-input-group">
                  <label
                    className="acc-modal-input-label"
                    htmlFor="withdrawAmountInput"
                  >
                    {isEn ? 'Withdrawal amount (₽):' : 'Сумма вывода (₽):'}
                  </label>
                  <input
                    type="number"
                    id="withdrawAmountInput"
                    className="acc-modal-input-control"
                    min="500"
                    max={currentBalance}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(Number(e.target.value))}
                    required
                  />
                </div>

                <div className="acc-modal-input-group">
                  <label
                    className="acc-modal-input-label"
                    htmlFor="withdrawMethodSelect"
                  >
                    {isEn ? 'Payment system:' : 'Платежная система:'}
                  </label>
                  <select
                    id="withdrawMethodSelect"
                    className="acc-modal-input-control"
                    value={withdrawMethod}
                    onChange={(e) => setWithdrawMethod(e.target.value)}
                  >
                    <option value="usdt">
                      {isEn ? 'USDT TRC-20 (0% Fee)' : 'USDT TRC-20 (Без комиссии)'}
                    </option>
                    <option value="card">Банковская карта (МИР / Visa / MC)</option>
                    <option value="sbp">СБП (Номер телефона)</option>
                    <option value="ton">TON Crystal</option>
                  </select>
                </div>

                <div className="acc-modal-input-group">
                  <label
                    className="acc-modal-input-label"
                    htmlFor="withdrawAddressInput"
                  >
                    {isEn
                      ? 'Details (Wallet address / Card number):'
                      : 'Реквизиты (Адрес кошелька / Номер карты):'}
                  </label>
                  <input
                    type="text"
                    id="withdrawAddressInput"
                    className="acc-modal-input-control"
                    placeholder="T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuW9..."
                    value={withdrawAddress}
                    onChange={(e) => setWithdrawAddress(e.target.value)}
                    required
                  />
                </div>

                <div className="acc-modal-btn-row">
                  <button
                    type="button"
                    className="btn-pill-secondary"
                    onClick={() => setIsWithdrawModalOpen(false)}
                  >
                    {isEn ? 'Cancel' : 'Отмена'}
                  </button>
                  <button type="submit" className="btn-pill-primary">
                    <span>{isEn ? 'Submit Request' : 'Создать заявку на вывод'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Restore Plan Modal */}
        <ModalWrapper isOpen={showRestoreModal} onClose={() => setShowRestoreModal(false)}>
          <div className="flex flex-col items-center text-center p-2">
            <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-5 shrink-0">
              <svg viewBox="0 0 24 24" className="w-7 h-7 stroke-purple-400 fill-none stroke-2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <h3 className="text-xl font-extrabold text-white mb-3">
              {isEn ? 'Plan Restoration' : 'Восстановление тарифа'}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {isEn
                ? 'Did your subscription disappear due to a recent update? We will restore it quickly! Just provide your Account ID and payment receipt.'
                : 'Пропал тариф из-за недавнего обновления? Вернём без проблем! Достаточно предоставить ID вашего аккаунта и чек об оплате.'}
            </p>

            {user?.id && (
              <div className="w-full bg-white/[0.02] border border-white/[0.05] rounded-xl px-4 py-3 mb-6 flex flex-col items-center justify-center font-mono">
                <span className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                  {isEn ? 'Your Account ID' : 'Ваш ID Аккаунта'}
                </span>
                <span className="text-base font-bold text-white">#{user.id}</span>
              </div>
            )}

            <div className="w-full h-px bg-white/[0.06] mb-6" />

            <div className="flex flex-col items-center gap-2 w-full">
              <span className="text-xs text-gray-500 uppercase tracking-wider">
                {isEn ? 'Email us at:' : 'Напишите нам на почту:'}
              </span>
              <a
                href="mailto:stormhackinfo@gmail.com"
                className="text-purple-400 hover:text-purple-300 font-semibold text-base transition-colors flex items-center gap-2 hover:underline"
              >
                stormhackinfo@gmail.com
              </a>
            </div>

            <button
              onClick={() => setShowRestoreModal(false)}
              className="mt-8 w-full py-3.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] hover:border-white/[0.1] text-gray-300 hover:text-white font-bold text-sm tracking-wide transition-all duration-150 active:scale-[0.98]"
            >
              {isEn ? 'CLOSE' : 'ЗАКРЫТЬ'}
            </button>
          </div>
        </ModalWrapper>
      </div>
    </PageTransition>
  );
};

export default AccountPage;